/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import dayjs from "dayjs";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then, When } from "react-if";
import { ExtendedTooltip, FlexContainer, Text, theme } from "@nordcloud/gnui";
import { IncomingEvent, IncomingEventStatus } from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { TooltipText } from "~/components/Styled/TooltipText";
import { TableWrapper } from "~/components/Tables";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import { dateFormat } from "~/constants";
import { useToggle } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { isNotEmpty, sortItem } from "~/tools";
import { convertMinutesToTime, RefetchOptions } from "~/views/events/helpers";
import { EventPopoverActions } from "../EventsDetails/components/EventPopoverActions";
import { EventDetailRow, EventsTableRow, GridColumns } from "../styled";
import { EntityType, PlanEntityIncomingEventList } from "../types";
import { planActionsColumns } from "./PlanActionsColumns";
import { TooltipIconStatus } from "./TooltipIconStatus";

type Props = {
  event: IncomingEvent;
};

export function IncomingEventItem({ event }: Props) {
  const [isExpanded, toggleIsExpanded] = useToggle();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const endTimeTZ = dayjs(event?.estimatedEndTime ?? "")
    .tz(event?.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const startTimeTZ = dayjs(event?.startTime ?? "")
    .tz(event?.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const duration = dayjs(event?.estimatedEndTime)
    .startOf("minutes")
    .diff(dayjs(event?.startTime).startOf("minutes"), "minutes");

  const planActions = event?.plan?.planActions ?? [];
  const planActionBatches = event.plan?.planActionBatches ?? [];

  const planEntities = [...planActions, ...planActionBatches].map((entity) => {
    const batchActions =
      entity.__typename === "PlanActionBatch" ? entity.batchActions : undefined;
    return {
      ...entity,
      subRows: batchActions ?? [],
    };
  });

  const sortedPlanEntities = sortItem(
    planEntities,
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  const hasEventEntities = isNotEmpty(planEntities);
  const isEventInProgress = event?.status === IncomingEventStatus.Upcoming;

  return (
    <EventsTableRow
      spacing="spacing02"
      boxStyle="lightGrey"
      data-testid="event-row"
    >
      <Row data-testid={event?.name}>
        <Col>
          <GridColumns>
            <FlexContainer justifyContent="center" alignItems="center">
              <When condition={hasEventEntities}>
                <ExpandIcon
                  name="chevronDown"
                  title="chevronDown"
                  isExpanded={isExpanded}
                  onClick={toggleIsExpanded}
                />
              </When>
            </FlexContainer>
            <TooltipIconStatus
              status={event?.status}
              type={EntityType.FUTURE_EVENT}
            />
            <NameWithLink
              name={event?.name ?? ""}
              link={ROUTES.events.generateDetails(event?.id ?? "")}
              containerStyle={{ minWidth: 0 }}
            />
            <EventDetailRow alignItems="center" data-label="Start">
              <ExtendedTooltip
                caption={
                  <>
                    Plan Time: {startTimeTZ}
                    <br />
                    Timezone: {event?.scheduleTimezone}
                  </>
                }
              >
                <TooltipText
                  size="md"
                  tag="span"
                  mr={theme.spacing.spacing01}
                  weight="regular"
                  color={theme.color.text.text01}
                >
                  {dayjs(event?.startTime).format(
                    dateFormat.dayMonthYearHourMinute
                  )}
                </TooltipText>
              </ExtendedTooltip>
            </EventDetailRow>
            <EventDetailRow alignItems="center" data-label="End">
              <ExtendedTooltip
                caption={
                  <>
                    Plan Time: {endTimeTZ}
                    <br />
                    Timezone: {event?.scheduleTimezone}
                    <br />
                    End Time will appear when the event is complete
                  </>
                }
              >
                <TooltipText
                  size="md"
                  tag="span"
                  mr={theme.spacing.spacing01}
                  weight="regular"
                  color={theme.color.text.text01}
                >
                  <If condition={isEventInProgress}>
                    <Then>Awaiting completion</Then>
                    <Else>
                      {dayjs(event?.estimatedEndTime).format(
                        dateFormat.dayMonthYearHourMinute
                      )}
                    </Else>
                  </If>
                </TooltipText>
              </ExtendedTooltip>
            </EventDetailRow>
            <EventDetailRow alignItems="center" data-label="Duration">
              <Text
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                {convertMinutesToTime(duration)}
              </Text>
            </EventDetailRow>
            <div />
            <FlexContainer alignItems="center" justifyContent="flex-end">
              <If condition={event.status !== IncomingEventStatus.Revoked}>
                <Then>
                  <EventPopoverActions
                    eventId={event?.id}
                    status={IncomingEventStatus.Upcoming}
                    refetchQueries={RefetchOptions.INCOMING_EVENTS}
                  />
                </Then>
              </If>
            </FlexContainer>
          </GridColumns>
          <When condition={isExpanded}>
            <TableWrapper<PlanEntityIncomingEventList>
              loading={false}
              data={sortedPlanEntities}
              columns={planActionsColumns({ status: event.status })}
              rowSelection={rowSelection}
              selectedRowsHandler={() => null}
              setRowSelection={setRowSelection}
              stripped
            />
          </When>
        </Col>
      </Row>
    </EventsTableRow>
  );
}
